import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    vapidPublicKey: String,
    subscribeUrl: String,
    successMessage: String
  }

  declare vapidPublicKeyValue: string
  declare subscribeUrlValue: string
  declare successMessageValue: string

  async connect() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      (this.element as HTMLElement).style.display = 'none';
      return;
    }

    try {
      // Register at /sw.js (root path) so the SW's default scope is "/"
      // and it can control all app pages (/you/{slug}, etc.).
      await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    } catch (error) {
      console.error('SW registration error:', error);
    }

    await this.updateButtonState();
  }

  // Single entry point — decides whether to subscribe or unsubscribe
  async toggle() {
    if (!('serviceWorker' in navigator)) return;

    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();

    if (existing) {
      await this.doUnsubscribe(existing);
    } else {
      await this.doSubscribe(registration);
    }

    await this.updateButtonState();
  }

  private async doSubscribe(registration: ServiceWorkerRegistration) {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    try {
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKeyValue)
      });

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const res = await fetch(this.subscribeUrlValue, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription, timezone })
      });

      if (!res.ok) {
        // Roll back browser subscription if server failed to save it
        const sub = await registration.pushManager.getSubscription();
        await sub?.unsubscribe();
        this.showError();
      } else {
        this.showSuccess();
      }
    } catch (e) {
      console.error('Subscribe error:', e);
      this.showError();
    }
  }

  private async doUnsubscribe(subscription: PushSubscription) {
    const endpoint = subscription.endpoint;

    try {
      await subscription.unsubscribe();

      const res = await fetch(this.subscribeUrlValue, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint })
      });

      if (!res.ok) {
        console.error('Unsubscribe request failed', res.status);
      }
    } catch (e) {
      console.error('Unsubscribe error:', e);
    }
  }

  private showSuccess() {
    const el = this.element as HTMLButtonElement;
    el.textContent = '✅';
    el.title = this.successMessageValue;
    setTimeout(() => this.updateButtonState(), 2000);
  }

  private showError() {
    const el = this.element as HTMLButtonElement;
    el.textContent = '❌';
    setTimeout(() => this.updateButtonState(), 2000);
  }

  private async updateButtonState() {
    const el = this.element as HTMLButtonElement;

    if (Notification.permission === 'denied') {
      el.style.display = 'none';
      return;
    }

    const registration = await navigator.serviceWorker.ready;
    const sub = await registration.pushManager.getSubscription();

    el.style.display = '';

    if (sub) {
      el.textContent = '🔕';
      el.title = 'Disable notifications';
      el.setAttribute('aria-label', 'Disable notifications');
    } else {
      el.textContent = '🔔';
      el.title = 'Enable notifications';
      el.setAttribute('aria-label', 'Enable notifications');
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}
